/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerStaffPage.tsx
 * PURPOSE: Màn Nhân viên — SC-012-MGR-STAFF (Release v0 WP-4)
 *          Bản chính "Quản lý chi nhánh": bảng đội ngũ 5 cột gom nhóm khu vực,
 *          NV chưa gán khu vực hiện flat lên đầu (ISS-MGR-6, KHÔNG sort lại ở
 *          FE ngoài thứ tự backend trả) + modal [D] Chi tiết NV (CHỈ ĐỌC)
 *          + 2026-08-17 (ISS-MGR-12 §36, BR-MGR-001 §5.5 — lát cắt REQ-A+REQ-B):
 *          tab bar mới [T1] Danh sách (mặc định, bảng cũ) · [T2] Yêu cầu nghỉ
 *          phép (quick-action, tái dùng POST /manager/bulk-approve đã live ở
 *          /manager/leave-approvals) · [T3] Giao việc (ground-up, nối
 *          backend/app/api/v1/tasks.py lần đầu từ phía Manager). REQ-C (trạng
 *          thái đang ca) + REQ-D (chat) KHÔNG nằm trong lượt này — xem BR §5.5.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Đội ngũ chi nhánh (Vietnamese F&B Workforce Management)
 * - Impact: UX Critical — xem roster, trạng thái chấm công hôm nay, drill sang
 *   Phân ca / Chấm công; quick-action duyệt nghỉ phép + giao việc ngay tại
 *   màn này (ISS-MGR-12). KHÔNG Thêm/Sửa/Reset/Vô hiệu NV (quyền TA ở SC-007)
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: React + TanStack Query (useManagerStaff / useManagerDashboard /
 *   useManagerTasks hooks) — mọi data từ API thật, loading skeleton + empty
 *   state + error + toast. [K-FORM]/[T3] verified live 2026-08-17 against
 *   real devdb (BR-MGR-001 §5.5): GET/POST /tasks/ + POST /bulk/assign đều
 *   200 với UUID payload thật (ISS-ARCH-20 fix xác nhận đã ăn).
 * - Dependencies: React Router, useManagerStaff, useManagerDashboard,
 *   useManagerTasks, useManagerHub, NQH design tokens
 *
 * @cross-references
 * - Mockup chuẩn: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/staff.html
 * - BR: docs/02-design/04-ui-ux-design/persona-flows/03-manager/BR-MGR-001-team-management-hub-2026-08-11.md
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/03-manager/PROPOSAL-SC-012-team-hub-redesign-2026-08-11.md
 * - Hooks: frontend/src/hooks/useManagerStaff.ts, useManagerDashboard.ts, useManagerTasks.ts, useManagerHub.ts
 * - Backend: backend/app/api/v2/endpoints/manager_staff.py, manager_approvals.py; backend/app/api/v1/tasks.py
 * - Sibling: frontend/src/pages/manager/ManagerAttendancePage.tsx (SC-011), ManagerApprovalQueue.tsx (/manager/leave-approvals)
 * ===============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useManagerStaff, useStaffWeekDetail } from "../../hooks/useManagerStaff";
import { usePendingApprovals, useBulkApprove } from "../../hooks/useManagerDashboard";
import { useManagerWeekSummary } from "../../hooks/useManagerHub";
import {
  useManagerTasksList,
  useCreateManagerTask,
  useAssignManagerTask,
  useBulkAssignManagerTasks,
} from "../../hooks/useManagerTasks";
import type {
  StaffListItem,
  StaffTodayStatus,
  PendingApprovalItem,
  ManagerTaskItem,
  ManagerTaskPriority,
  ManagerTaskStatus,
} from "../../services/api/managerApi";
import { useAuthStore } from "../../store/auth";
// F-15 fix: menu ⁝ dòng cuối qua PortalMenu (cùng pattern F-6/7/8)
import { PortalMenu, type PortalMenuAnchor } from "../../components/common/PortalMenu";
// ISS-MGR-3 (2026-08-09): tooltip ⓘ/tên qua PortalTooltip — cùng root cause
// .staffWrap { overflow-x:auto } cắt CSS `[data-tip]:hover::after` cũ.
import { PortalTooltip } from "../../components/common/PortalTooltip";
import { TimeTextInput } from "../../components/ui/TimeTextInput";
import styles from "./ManagerStaffPage.module.css";

// ── Inline SVG icons (Heroicons — copy nguyên path từ mockup, 0 emoji) ──────

const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={14} height={14} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

const SearchIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const DotsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18} aria-hidden="true">
    <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ISS-MGR-12 (BR-MGR-001 §5.5) — icon mới cho [T2]/[T3], cùng convention
// "inline SVG, 0 emoji" như trên (KHÔNG dùng ⏳/✔/+ Unicode literal).
const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const CheckIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const ClockIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ── Helpers ────────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");

/** ISO datetime → HH:MM (local) */
const formatTime = (iso: string | null) => {
  if (!iso) return "—:—";
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** yyyy-mm-dd → dd/mm */
const formatDayMonth = (iso: string) => {
  const [, mm, dd] = iso.split("-");
  return `${dd}/${mm}`;
};

/** yyyy-mm-dd → dd/mm/yyyy (BR-MGR-001 [T2] date range) */
const formatFullDate = (iso: string) => {
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

/** ISO datetime (task.due_date) → dd/mm HH:MM (local) — BR-MGR-001 [T3] */
const formatTaskDueDate = (iso: string) => {
  const d = new Date(iso);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Bỏ dấu tiếng Việt — tìm theo tên khớp cả không dấu (mockup [1]) */
const noDiacritics = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Số điện thoại → dạng gọi được (tel:/sms:) — chỉ giữ số và + đầu */
const phoneDialable = (phone: string) => phone.replace(/(?!^\+)\D/g, "");

/** Tooltip ⓘ verbatim mockup staff.html thead */
const COL_TOOLTIPS = {
  role: "Nhãn hiển thị theo cấp đơn vị được gán (vd Trưởng ca, Bếp trưởng, Nhân viên).",
  today:
    "Tình trạng chấm công hôm nay: ● đúng giờ · ▲ đi trễ · ▼ về sớm · ⚠ thiếu chấm hoặc lệch vị trí · ○ chưa tới ca · — không có ca: hôm nay không được xếp ca.",
};

/** Nhãn trạng thái VI — CHỮ + MÀU, không ký hiệu (ISS-MGR-18 lượt 2, đồng bộ
 * ManagerAttendancePage.tsx: chip màu ● herb · ▲ turmeric-700 · ⚠ chili · xám)
 *
 * Key gồm cả `StaffTodayStatus` (chip theo NGƯỜI, dòng roster) lẫn
 * `"unscheduled"` (bổ sung 2026-08-17, thuộc `AttendanceRowStatus` — panel
 * "Chấm công hôm nay" trong chi tiết NV hiện PER-CA, 1 NV có thể có ca
 * "ngoài lịch" dù bảng roster tổng vẫn tính theo NGƯỜI). 2 type khác nhau,
 * dùng chung 1 bảng nhãn cho gọn thay vì tách 2 Record trùng lặp. */
const STATUS_META: Record<StaffTodayStatus | "unscheduled", { label: string; cls: string }> = {
  on_time: { label: "Đúng giờ", cls: styles.stOk },
  late: { label: "Đi trễ", cls: styles.stLate },
  early_leave: { label: "Về sớm", cls: styles.stLate },
  missing: { label: "Thiếu chấm công", cls: styles.stBad },
  gps_deviation: { label: "GPS lệch", cls: styles.stBad },
  upcoming: { label: "Chưa tới ca", cls: styles.stNone },
  no_shift: { label: "Không có ca", cls: styles.stNone },
  unscheduled: { label: "Ngoài lịch", cls: styles.stLate },
};

/** Nhãn thứ trong tuần (T2..CN) — thứ tự backend trả (T2 đầu) */
const WEEKDAY_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

// ── BR-MGR-001 REQ-A ([T2] Yêu cầu nghỉ phép) ────────────────────────────

/** Nhãn VI LeaveType — SSOT: docs/02-design/04-ui-ux-design/UI-LABELS-VI-ENUM.md §8
 * (5 giá trị canonical, khớp backend/app/models/leave_request.py:44-55). Rule 6:
 * KHÔNG tự sáng tác — bảng này CHÉP lại SSOT đã ratify, không phải bịa mới
 * (khác `TaskCategory` bên dưới — SSOT đó CHƯA có, xem ghi chú tại TASK_CATEGORY). */
const LEAVE_TYPE_VI: Record<string, string> = {
  annual: "Nghỉ phép năm",
  public_holiday: "Nghỉ lễ",
  wedding: "Nghỉ kết hôn",
  funeral: "Nghỉ tang gia",
  child_wedding: "Nghỉ con kết hôn",
};

const LEAVE_CARD_CAP = 5;

// ── BR-MGR-001 REQ-B ([T3] Giao việc) ────────────────────────────────────

/** TaskPriority VI — BR §7 đề xuất nguyên văn (bản dịch trực tiếp), dùng được
 * ngay cho v0 (khác TaskCategory — BR để trống "?", chưa ratify). */
const TASK_PRIORITY_VI: Record<ManagerTaskPriority, string> = {
  LOW: "Thấp",
  MEDIUM: "Trung bình",
  HIGH: "Cao",
  URGENT: "Khẩn cấp",
};

const TASK_PRIORITY_CLASS: Record<ManagerTaskPriority, string> = {
  LOW: styles.prioLow,
  MEDIUM: styles.prioMedium,
  HIGH: styles.prioHigh,
  URGENT: styles.prioHigh,
};

/** TaskStatus VI — BR §7, đủ 6 giá trị enum thật (kể cả CANCELLED, không hiện
 * trong filter [K2] v0 nhưng badge/⋮ menu có thể cần). */
const TASK_STATUS_VI: Record<ManagerTaskStatus, string> = {
  PENDING: "Chưa giao",
  ASSIGNED: "Đã giao",
  IN_PROGRESS: "Đang làm",
  IN_REVIEW: "Chờ duyệt",
  COMPLETED: "Hoàn thành",
  CANCELLED: "Đã huỷ",
};

/** [K2] filter dropdown — wireframe §3.1: chỉ 5/6 giá trị (bỏ CANCELLED). */
const TASK_STATUS_FILTER_OPTIONS: Array<{ value: ManagerTaskStatus | ""; label: string }> = [
  { value: "", label: "Tất cả" },
  { value: "PENDING", label: "Chưa giao" },
  { value: "ASSIGNED", label: "Đã giao" },
  { value: "IN_PROGRESS", label: "Đang làm" },
  { value: "IN_REVIEW", label: "Chờ duyệt" },
  { value: "COMPLETED", label: "Hoàn thành" },
];

// BR §5 open item 1 + wireframe §7: TaskCategory (8 giá trị) CHƯA có nhãn VI
// trong UI-LABELS-VI-ENUM.md — @designer/@cpo ratify trước (Rule 6). "Danh
// mục" bị BỎ khỏi form v0 (không tự bịa nhãn) — task tạo ra có category mặc
// định OTHER (schema default, backend/app/schemas/task.py TaskBase.category).
// Nối lại field này khi SSOT có nhãn thật.

interface StaffGroup {
  key: string;
  areaName: string;
  items: StaffListItem[];
}

// ── Component ──────────────────────────────────────────────────────────────

const ManagerStaffPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [search, setSearch] = useState("");
  const [areaId, setAreaId] = useState<string>("");
  const [openMenu, setOpenMenu] = useState<"export" | "area" | "taskStatus" | null>(null);
  const [openRowMenu, setOpenRowMenu] = useState<string | null>(null);
  // F-15 fix: anchor rect cho PortalMenu
  const [menuAnchor, setMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ── BR-MGR-001 §5.5 — [T1]/[T2]/[T3] tab bar (wireframe §0.1: amendment
  // tại chỗ, KHÔNG route riêng; roster [T1] mặc định mỗi lần vào màn) ──────
  const [activeTab, setActiveTab] = useState<"list" | "leave" | "tasks">("list");

  // [T2] Yêu cầu nghỉ phép (REQ-A)
  const [rejectOpenId, setRejectOpenId] = useState<string | null>(null);
  const [rejectReasonText, setRejectReasonText] = useState("");
  const [leaveBusyId, setLeaveBusyId] = useState<string | null>(null);

  // [T3] Giao việc (REQ-B)
  const [taskStatusFilter, setTaskStatusFilter] = useState<ManagerTaskStatus | "">("");
  const [taskRowMenuId, setTaskRowMenuId] = useState<string | null>(null);
  const [taskMenuAnchor, setTaskMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  const [taskDetailItem, setTaskDetailItem] = useState<ManagerTaskItem | null>(null);
  const [reassignItem, setReassignItem] = useState<ManagerTaskItem | null>(null);
  const [reassignTarget, setReassignTarget] = useState("");
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [taskFormError, setTaskFormError] = useState<string | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    priority: "MEDIUM" as ManagerTaskPriority,
    due_date: "",
    due_time: "",
    assigned_to_id: "",
  });
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [bulkReassignTarget, setBulkReassignTarget] = useState("");

  const listQuery = useManagerStaff();
  const weekQuery = useStaffWeekDetail(detailUserId);
  const weekSummaryQuery = useManagerWeekSummary();

  // [T2] — badge count phải luôn tải (kể cả khi [T3]/[T1] đang mở), không chỉ khi tab active.
  const pendingLeaveQuery = usePendingApprovals({ approval_type: "leave_request", page_size: LEAVE_CARD_CAP });
  const bulkApproveMutation = useBulkApprove();

  // [T3]
  const tasksQuery = useManagerTasksList();
  const createTaskMutation = useCreateManagerTask();
  const assignTaskMutation = useAssignManagerTask();
  const bulkAssignMutation = useBulkAssignManagerTasks();

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Đóng dropdown/menu khi click ra ngoài (pattern G.close() common.js)
  useEffect(() => {
    if (!openMenu && !openRowMenu && !taskRowMenuId) return;
    const close = () => {
      setOpenMenu(null);
      setOpenRowMenu(null);
      setTaskRowMenuId(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenu, openRowMenu, taskRowMenuId]);

  const data = listQuery.data;

  // [T3] roster đã tải (KHÔNG theo filter [1]/[2]) — dùng chung cho dropdown
  // "Giao cho"/"Chuyển N việc cho" + tra tên hiển thị cột "Giao cho" (list
  // GET /tasks/ không trả assigned_to_name — xem managerApi.ts ManagerTaskItem).
  const rosterItems = data?.items ?? [];
  const rosterNameById = useMemo(() => {
    const map: Record<string, string> = {};
    rosterItems.forEach((item) => { map[item.user_id] = item.full_name; });
    return map;
  }, [rosterItems]);

  // [K2] filter — client-side trên danh sách đã tải (wireframe §3.1: không
  // thêm param server-side cho v0, cùng pattern "filter on already-loaded
  // data" của SC-009).
  const taskItems = tasksQuery.data ?? [];
  const filteredTasks = useMemo(
    () => (taskStatusFilter ? taskItems.filter((t) => t.status === taskStatusFilter) : taskItems),
    [taskItems, taskStatusFilter],
  );

  // Gom nhóm khu vực theo thứ tự roster (backend đã xếp: nhóm "Chưa phân khu
  // vực" đứng đầu — ISS-MGR-6 REOPEN 2026-08-10 — sau đó khu vực → tên)
  const groups = useMemo<StaffGroup[]>(() => {
    const result: StaffGroup[] = [];
    const byKey: Record<string, StaffGroup> = {};
    (data?.items ?? []).forEach((item) => {
      const key = item.work_area_id ?? "unassigned";
      if (!byKey[key]) {
        byKey[key] = { key, areaName: item.work_area_name, items: [] };
        result.push(byKey[key]);
      }
      byKey[key].items.push(item);
    });
    return result;
  }, [data]);

  // Options khu vực [2] — từ roster đầy đủ (không theo filter)
  const areaOptions = useMemo(
    () => groups.map((g) => ({ id: g.key === "unassigned" ? "" : g.key, name: g.areaName })),
    [groups],
  );

  // [1] Tìm theo tên (không dấu) + [2] Khu vực — giữ nguyên thứ tự roster,
  // ẩn nhóm rỗng sau lọc (chỉ đạo @coder trong mockup)
  const visibleGroups = useMemo<StaffGroup[]>(() => {
    const q = noDiacritics(search.trim());
    return groups
      .filter((g) => !areaId || g.key === areaId)
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) => !q || noDiacritics(item.full_name).includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, search, areaId]);

  // ── Handlers ──

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const openDetail = (item: StaffListItem) => {
    setOpenRowMenu(null);
    setDetailUserId(item.user_id);
  };

  const goSchedule = () => navigate("/manager/schedule");
  const goAttendance = () => navigate("/manager/attendance");

  // [8m] Xuất ▼ — client-side (cùng pattern TenantUserManagement.tsx/
  // OrganizationManagement/index.tsx), xuất đúng danh sách đang lọc
  // (visibleGroups: theo tìm-tên + khu vực đang chọn), không phải toàn roster.
  const handleExport = (format: "xlsx" | "csv") => {
    setOpenMenu(null);
    const rows = visibleGroups.flatMap((g) => g.items);
    if (rows.length === 0) {
      setToast("Không có nhân viên nào để xuất.");
      return;
    }
    const data = rows.map((item) => ({
      "Mã NV": item.employee_code ?? "",
      "Họ tên": item.full_name,
      "Bộ phận": item.work_area_name,
      "Vai trò": item.role_label,
      "Điện thoại": item.phone ?? "",
      "Trạng thái hôm nay": (STATUS_META[item.today_status] ?? STATUS_META.no_shift).label,
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === "xlsx") {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Nhan vien");
      XLSX.writeFile(wb, `nhan-vien-${stamp}.xlsx`);
    } else {
      const headers = Object.keys(data[0]);
      const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
      const csv =
        "﻿" +
        [headers.join(","), ...data.map((r) => headers.map((h) => escape((r as Record<string, string>)[h] ?? "")).join(","))].join(
          "\r\n",
        );
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nhan-vien-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    }
    setToast(`Đã xuất ${rows.length} nhân viên (${format === "xlsx" ? "Excel" : "CSV"}).`);
  };

  // ── [T2] Handlers — REQ-A (wireframe §2.2/§2.3) ──

  const handleApproveLeave = async (item: PendingApprovalItem) => {
    setLeaveBusyId(item.id);
    try {
      await bulkApproveMutation.mutateAsync({
        item_ids: [item.id],
        approval_type: "leave_request",
        action: "approve",
      });
      setToast(`Đã duyệt đơn của ${item.requester_name}`);
      pendingLeaveQuery.refetch();
    } catch {
      setToast("Không duyệt được. Thử lại");
    } finally {
      setLeaveBusyId(null);
    }
  };

  const openRejectLeave = (item: PendingApprovalItem) => {
    setRejectOpenId(item.id);
    setRejectReasonText("");
  };

  const cancelRejectLeave = () => {
    setRejectOpenId(null);
    setRejectReasonText("");
  };

  const confirmRejectLeave = async (item: PendingApprovalItem) => {
    if (!rejectReasonText.trim()) return;
    setLeaveBusyId(item.id);
    try {
      await bulkApproveMutation.mutateAsync({
        item_ids: [item.id],
        approval_type: "leave_request",
        action: "reject",
        reason: rejectReasonText.trim(),
      });
      setToast(`Đã từ chối đơn của ${item.requester_name}`);
      setRejectOpenId(null);
      setRejectReasonText("");
      pendingLeaveQuery.refetch();
    } catch {
      setToast("Không xử lý được. Thử lại");
    } finally {
      setLeaveBusyId(null);
    }
  };

  // ── [T3] Handlers — REQ-B (wireframe §3) ──

  const resetTaskForm = () => {
    setTaskForm({ title: "", description: "", priority: "MEDIUM", due_date: "", due_time: "", assigned_to_id: "" });
    setTaskFormError(null);
  };

  const openCreateTask = () => {
    resetTaskForm();
    setShowCreateTask(true);
  };

  const closeCreateTask = () => {
    setShowCreateTask(false);
    resetTaskForm();
  };

  const submitCreateTask = async () => {
    const title = taskForm.title.trim();
    if (!title) return;
    // BR-MGR-001 §1.3.1 — branch_id BẮT BUỘC, lấy từ chính Manager đang đăng
    // nhập (không phải chọn tay) — thiếu field này khiến việc vừa tạo biến
    // mất khỏi chính danh sách của Manager (đã verify thật trên devdb).
    if (!user?.branch_id) {
      setTaskFormError("Không xác định được chi nhánh của bạn. Thử tải lại trang.");
      return;
    }
    let due_date: string | undefined;
    if (taskForm.due_date) {
      const time = taskForm.due_time || "00:00";
      const iso = new Date(`${taskForm.due_date}T${time}:00`);
      if (!Number.isNaN(iso.getTime())) due_date = iso.toISOString();
    }
    try {
      setTaskFormError(null);
      await createTaskMutation.mutateAsync({
        title,
        description: taskForm.description.trim() || undefined,
        priority: taskForm.priority,
        due_date,
        branch_id: user.branch_id,
        assigned_to_id: taskForm.assigned_to_id || undefined,
      });
      setToast(taskForm.assigned_to_id ? "Đã giao việc" : "Đã tạo việc");
      setShowCreateTask(false);
      resetTaskForm();
    } catch {
      setTaskFormError("Không tạo được việc. Thử lại");
    }
  };

  const openReassign = (item: ManagerTaskItem) => {
    setTaskRowMenuId(null);
    setReassignItem(item);
    setReassignTarget(item.assigned_to_id ?? "");
  };

  const closeReassign = () => {
    setReassignItem(null);
    setReassignTarget("");
  };

  const confirmReassign = async () => {
    if (!reassignItem || !reassignTarget) return;
    try {
      await assignTaskMutation.mutateAsync({
        taskId: reassignItem.id,
        payload: { assigned_to_id: reassignTarget },
      });
      const name = rosterNameById[reassignTarget] ?? "nhân viên";
      setToast(`Đã giao lại việc cho ${name}`);
      setReassignItem(null);
      setReassignTarget("");
    } catch {
      setToast("Không giao lại được. Thử lại");
    }
  };

  const toggleTaskSelect = (taskId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const clearTaskSelection = () => {
    setSelectedTaskIds(new Set());
    setBulkReassignTarget("");
  };

  const confirmBulkReassign = async () => {
    if (selectedTaskIds.size === 0 || !bulkReassignTarget) return;
    try {
      const task_ids = Array.from(selectedTaskIds);
      await bulkAssignMutation.mutateAsync({ task_ids, assigned_to_id: bulkReassignTarget });
      const name = rosterNameById[bulkReassignTarget] ?? "nhân viên";
      setToast(`Đã chuyển ${task_ids.length} việc cho ${name}`);
      clearTaskSelection();
    } catch {
      setToast("Không chuyển được. Thử lại");
    }
  };

  // ── Render helpers ──

  const renderStatusChip = (status: StaffTodayStatus) => {
    const meta = STATUS_META[status] ?? STATUS_META.no_shift;
    return <span className={`${styles.st} ${meta.cls}`}>{meta.label}</span>;
  };

  const renderRowMenu = (item: StaffListItem) => {
    const menuOpen = openRowMenu === item.user_id;
    return (
      <td className={styles.colAct}>
        <span className={styles.menuAnchor}>
          <button
            type="button"
            className={styles.btnIcon}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label={`Thao tác — ${item.full_name}`}
            onClick={(e) => {
              e.stopPropagation();
              if (menuOpen) { setOpenRowMenu(null); return; }
              const r = e.currentTarget.getBoundingClientRect();
              setMenuAnchor({ top: r.top, bottom: r.bottom, right: window.innerWidth - r.right });
              setOpenRowMenu(item.user_id);
            }}
          >
            <DotsIcon />
          </button>
          {/* F-15 fix: portal + fixed — không bị .staffWrap overflow cắt */}
          {menuOpen && menuAnchor && (
            <PortalMenu anchor={menuAnchor} onClose={() => setOpenRowMenu(null)}>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => openDetail(item)}>
                Xem chi tiết
              </button>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={goSchedule}>
                Xem lịch
              </button>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={goAttendance}>
                Xem chấm công
              </button>
            </PortalMenu>
          )}
        </span>
      </td>
    );
  };

  /** [5] Bảng đội ngũ 5 cột, gom nhóm khu vực ▾ */
  const renderTable = () => (
    <div className={styles.staffWrap}>
      <table className={styles.staffTable}>
        <thead>
          <tr>
            <th scope="col">Mã NV</th>
            <th scope="col">Họ tên</th>
            <th scope="col">
              Vai trò{" "}
              <PortalTooltip text={COL_TOOLTIPS.role} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.role}>
                <InfoIcon />
              </PortalTooltip>
            </th>
            <th scope="col">Điện thoại</th>
            <th scope="col">
              Hôm nay{" "}
              <PortalTooltip text={COL_TOOLTIPS.today} className={styles.infoIco} tabIndex={0} ariaLabel={COL_TOOLTIPS.today}>
                <InfoIcon />
              </PortalTooltip>
            </th>
            <th className={styles.colAct} scope="col">Thao tác</th>
          </tr>
        </thead>
        <tbody>
          {visibleGroups.map((group) => {
            // ISS-MGR-6 (2026-08-09, Hang Le chốt): NV không gán khu vực
            // (key "unassigned") hiện THẲNG, KHÔNG group/heading riêng — sửa
            // lại kết luận PASS cũ (2 agent trước chỉ kiểm "không bị ẩn",
            // yêu cầu thật là "không đóng khung nhóm", khác nhau).
            const isUnassigned = group.key === "unassigned";
            const isCollapsed = !isUnassigned && !!collapsed[group.key];
            const rows = group.items.map((item) => (
              <tr key={item.user_id}>
                <td className={styles.colCode}>{item.employee_code ?? "—"}</td>
                {/* ISS-MGR-14 (2026-08-09, @designer chốt): bỏ tooltip tên NV —
                    cột "Họ tên" wrap 2 dòng thay vì truncate+tooltip (.colName). */}
                <td className={styles.colName}>
                  {item.full_name}
                  {item.is_cross_branch_assignment && (
                    <div className={styles.crossBranchBadge}>
                      🔄 Điều động từ {item.home_branch_name ?? "chi nhánh khác"}
                    </div>
                  )}
                </td>
                <td>{item.role_label}</td>
                <td>{item.phone ?? "—"}</td>
                <td>{renderStatusChip(item.today_status)}</td>
                {renderRowMenu(item)}
              </tr>
            ));
            if (isUnassigned) {
              return <React.Fragment key={group.key}>{rows}</React.Fragment>;
            }
            return (
              <React.Fragment key={group.key}>
                <tr className={styles.deptGroup}>
                  <th colSpan={6} scope="colgroup">
                    <button
                      type="button"
                      className={styles.deptToggle}
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleGroup(group.key)}
                    >
                      <span className={`${styles.deptToggleChev} ${isCollapsed ? styles.deptToggleChevCollapsed : ""}`} aria-hidden="true">
                        <ChevronDownIcon />
                      </span>
                      {group.areaName} <span className={styles.deptCount}>({group.items.length})</span>
                    </button>
                  </th>
                </tr>
                {!isCollapsed && rows}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  /** Mobile <768: card mỗi nhân viên (R5 bảng→card) */
  const renderCards = () => (
    <div className={styles.staffCards}>
      {visibleGroups.map((group) => (
        <React.Fragment key={group.key}>
          {/* ISS-MGR-6: NV không gán khu vực (key "unassigned") hiện thẳng */}
          {group.key !== "unassigned" && (
            <div className={styles.deptCardsHead}>
              <span className={styles.deptToggleChev} aria-hidden="true"><ChevronDownIcon /></span>
              {group.areaName} <span className={styles.deptCount}>({group.items.length})</span>
            </div>
          )}
          {group.items.map((item) => (
            <div className={styles.staffCard} key={item.user_id}>
              <div className={styles.staffCardHead}>
                <span className={styles.staffCardName}>
                  {item.employee_code ? `[${item.employee_code}] ` : ""}
                  {item.full_name}
                </span>
                <button
                  type="button"
                  className={styles.btnIcon}
                  aria-label={`Xem chi tiết — ${item.full_name}`}
                  onClick={() => openDetail(item)}
                >
                  <DotsIcon />
                </button>
              </div>
              <div className={styles.staffCardLine}>
                {item.role_label} · {item.phone ?? "—"}
              </div>
              {item.is_cross_branch_assignment && (
                <div className={styles.crossBranchBadge}>
                  🔄 Điều động từ {item.home_branch_name ?? "chi nhánh khác"}
                </div>
              )}
              <div className={styles.staffCardLine}>{renderStatusChip(item.today_status)}</div>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );

  /** [D] Modal "Chi tiết nhân viên" — CHỈ ĐỌC ~520px */
  const renderDetailModal = () => {
    if (!detailUserId) return null;
    const detail = weekQuery.data;
    const branchLabel = detail?.branch_code || detail?.branch_name || "";
    const dialPhone = detail?.phone ? phoneDialable(detail.phone) : null;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="staffDetailTitle">
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="staffDetailTitle">
              {detail?.full_name ?? "Chi tiết nhân viên"}
            </h2>
            <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={() => setDetailUserId(null)}>
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalBody}>
            {weekQuery.isLoading ? (
              <div aria-busy="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className={`${styles.skeleton} ${styles.skeletonLine}`} />
                ))}
              </div>
            ) : weekQuery.isError || !detail ? (
              <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
                Không tải được dữ liệu. Thử lại
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                  style={{ marginLeft: "auto" }}
                  onClick={() => weekQuery.refetch()}
                >
                  Thử lại
                </button>
              </div>
            ) : (
              <>
                <div className={styles.dGrid}>
                  <div>
                    Bộ phận: {detail.work_area_name}
                    {branchLabel ? ` · ${branchLabel}` : ""}
                  </div>
                  <div>Vai trò: {detail.role_label}</div>
                  <div className={styles.dContact}>
                    <span>Điện thoại: {detail.phone ?? "—"}</span>
                    {dialPhone && (
                      <>
                        {/* [Gọi]/[Nhắn] → app điện thoại (tel:/sms:) */}
                        <a className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} href={`tel:${dialPhone}`}>
                          Gọi
                        </a>
                        <a className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} href={`sms:${dialPhone}`}>
                          Nhắn
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <div className={styles.dSec}>
                  <div className={styles.dSecTitle}>Ca tuần này (Tuần {detail.week_number})</div>
                  <div className={styles.dWeekGrid}>
                    {detail.days.map((day, idx) => {
                      const codes = day.shifts
                        .map((s) => {
                          const code = s.shift_code ?? s.shift_label;
                          return s.scheduled_start && s.scheduled_end
                            ? `${code} ${s.scheduled_start.slice(0, 5)}-${s.scheduled_end.slice(0, 5)}`
                            : code;
                        })
                        .join(" + ");
                      return (
                        <div className={styles.dWeekRow} key={idx}>
                          <span className={styles.dWeekDay}>{WEEKDAY_LABELS[idx] ?? ""}</span>
                          <span className={styles.dWeekShift}>{codes || "—"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={styles.dSec}>
                  <div className={styles.dSecTitle}>
                    Chấm công hôm nay {formatDayMonth(detail.today)}
                  </div>
                  {detail.today_shifts.length === 0 ? (
                    <div>— Không có ca hôm nay</div>
                  ) : (
                    detail.today_shifts.map((shift) => {
                      const meta = STATUS_META[shift.status] ?? STATUS_META.upcoming;
                      return (
                        <div className={styles.dShiftLine} key={shift.schedule_id}>
                          <div className={styles.dShiftCode}>{shift.shift_code ?? shift.shift_label}</div>
                          <div className={styles.dShiftMeta}>
                            <span>{formatTime(shift.clock_in)}–{formatTime(shift.clock_out)}</span>
                            <span className={`${styles.st} ${meta.cls}`}>
                              {meta.label}
                              {shift.clock_in != null && shift.status !== "upcoming" && (
                                <span className={styles.stSub}>
                                  {shift.is_approved ? " · Đã duyệt" : " · Chờ duyệt"}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className={styles.dSec}>
                  <div className={styles.dSecTitle}>Đúng giờ tháng này</div>
                  {detail.on_time_percent_month == null ? (
                    <div>— Chưa có ca nào trong tháng</div>
                  ) : (
                    <div>
                      {detail.on_time_percent_month}%
                      <span className={styles.dContact}>
                        {" "}
                        ({(detail.month_shift_count ?? 0) - (detail.month_late_count ?? 0) - (detail.month_early_count ?? 0)}/
                        {detail.month_shift_count} ca đúng giờ — {detail.month_late_count ?? 0} trễ, {detail.month_early_count ?? 0} về sớm)
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => { setDetailUserId(null); goSchedule(); }}>
              Xem lịch →
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setDetailUserId(null); goAttendance(); }}>
              Xem chấm công →
            </button>
          </div>
        </div>
      </div>
    );
  };

  /** [T2] "Yêu cầu nghỉ phép" — REQ-A, wireframe §2. */
  const renderLeaveTab = () => {
    const branchLabel = [weekSummaryQuery.data?.branch_name, weekSummaryQuery.data?.branch_code]
      .filter(Boolean)
      .join(" · ");
    const leaveData = pendingLeaveQuery.data;
    const items = leaveData?.items ?? [];
    const totalCount = leaveData?.leave_request_count ?? 0;
    const moreCount = Math.max(0, totalCount - items.length);

    return (
      <div className={styles.leavePanel}>
        {/* [L1] tóm tắt + [L2] CTA */}
        <div className={styles.leaveSummaryRow}>
          <span className={styles.leaveSummaryText}>
            {pendingLeaveQuery.isLoading
              ? "Đang tải…"
              : `${totalCount} đơn chờ duyệt${branchLabel ? ` tại ${branchLabel}` : ""}`}
          </span>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            onClick={() => navigate("/manager/leave-approvals")}
          >
            Xem tất cả tại Duyệt đơn →
          </button>
        </div>

        {/* [L3] card list */}
        {pendingLeaveQuery.isLoading ? (
          <div aria-busy="true">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} style={{ height: 84, marginBottom: 8 }} />
            ))}
          </div>
        ) : pendingLeaveQuery.isError ? (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
            Không tải được danh sách đơn · Thử lại
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
              style={{ marginLeft: "auto" }}
              onClick={() => pendingLeaveQuery.refetch()}
            >
              Thử lại
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>Không có đơn nghỉ phép chờ duyệt.</div>
        ) : (
          <div className={styles.leaveCards}>
            {items.map((item) => {
              const meta = item.metadata as {
                leave_type?: string;
                start_date?: string;
                end_date?: string;
                total_days?: number;
                reason?: string;
              };
              const leaveTypeLabel = meta.leave_type ? (LEAVE_TYPE_VI[meta.leave_type] ?? meta.leave_type) : "";
              const dateRange = meta.start_date && meta.end_date
                ? (meta.start_date === meta.end_date
                    ? formatFullDate(meta.start_date)
                    : `${formatDayMonth(meta.start_date)} → ${formatFullDate(meta.end_date)}`)
                : "";
              const busy = leaveBusyId === item.id;
              const rejecting = rejectOpenId === item.id;
              return (
                <div className={styles.leaveCard} key={item.id}>
                  <div className={styles.leaveCardName}>
                    {item.requester_name} · {leaveTypeLabel}
                  </div>
                  <div className={styles.leaveCardLine}>
                    {dateRange}
                    {meta.total_days != null ? ` · ${meta.total_days} ngày làm việc` : ""}
                  </div>
                  {meta.reason && <div className={styles.leaveCardLine}>Lý do: "{meta.reason}"</div>}

                  {rejecting ? (
                    <div className={styles.rejectForm}>
                      <textarea
                        className={styles.rejectTextarea}
                        placeholder="Lý do từ chối (bắt buộc)…"
                        value={rejectReasonText}
                        onChange={(e) => setRejectReasonText(e.target.value)}
                        disabled={busy}
                      />
                      <div className={styles.leaveCardActions}>
                        <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={cancelRejectLeave} disabled={busy}>
                          Huỷ
                        </button>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                          disabled={busy || !rejectReasonText.trim()}
                          onClick={() => confirmRejectLeave(item)}
                        >
                          {busy ? "Đang xử lý…" : "Xác nhận từ chối"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.leaveCardActions}>
                      <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} disabled={busy} onClick={() => openRejectLeave(item)}>
                        Từ chối
                      </button>
                      <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} disabled={busy} onClick={() => handleApproveLeave(item)}>
                        {busy ? "Đang xử lý…" : "Duyệt"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {moreCount > 0 && (
              <button
                type="button"
                className={styles.leaveMoreLink}
                onClick={() => navigate("/manager/leave-approvals")}
              >
                +{moreCount} đơn khác — xem tại Duyệt đơn
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  /** [T3] "Giao việc" — REQ-B, wireframe §3. */
  const renderTasksTab = () => {
    const selectedCount = selectedTaskIds.size;
    return (
      <div className={styles.taskPanel}>
        {selectedCount > 0 ? (
          // [K3] bulk bar — thay thế toolbar mặc định khi có dòng được chọn
          // (wireframe §3.1: "selection toolbar replaces the default toolbar row")
          <div className={styles.bulkBar}>
            <span className={styles.bulkBarLabel}>Đã chọn {selectedCount} việc</span>
            <select
              className={styles.selectBtn}
              value={bulkReassignTarget}
              onChange={(e) => setBulkReassignTarget(e.target.value)}
              aria-label="Chọn nhân viên"
            >
              <option value="">Chọn nhân viên…</option>
              {rosterItems.map((r) => (
                <option key={r.user_id} value={r.user_id}>{r.full_name}</option>
              ))}
            </select>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={!bulkReassignTarget || bulkAssignMutation.isPending}
              onClick={confirmBulkReassign}
            >
              {bulkAssignMutation.isPending ? "Đang chuyển…" : `Chuyển ${selectedCount} việc cho 1 nhân viên`}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={clearTaskSelection}>
              Huỷ chọn
            </button>
          </div>
        ) : (
          // [K1] + [K2] toolbar mặc định
          <div className={styles.taskToolbar}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openCreateTask}>
              <PlusIcon /> Giao việc mới
            </button>
            <span className={styles.menuAnchor}>
              <button
                type="button"
                className={styles.selectBtn}
                aria-haspopup="true"
                aria-expanded={openMenu === "taskStatus"}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenu(openMenu === "taskStatus" ? null : "taskStatus");
                }}
              >
                <span>
                  Trạng thái: {TASK_STATUS_FILTER_OPTIONS.find((o) => o.value === taskStatusFilter)?.label ?? "Tất cả"}
                </span>
                <ChevronDownIcon />
              </button>
              {openMenu === "taskStatus" && (
                <div className={styles.menu} role="menu" aria-label="Lọc trạng thái việc">
                  {TASK_STATUS_FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.value || "all"}
                      type="button"
                      className={`${styles.menuItem} ${taskStatusFilter === opt.value ? styles.menuItemActive : ""}`}
                      role="menuitem"
                      onClick={() => { setTaskStatusFilter(opt.value); setOpenMenu(null); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </span>
            <span className={styles.taskCount}>{filteredTasks.length} việc</span>
          </div>
        )}

        {tasksQuery.isLoading ? (
          <div aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} />
            ))}
          </div>
        ) : tasksQuery.isError ? (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
            Không tải được danh sách việc · Thử lại
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
              style={{ marginLeft: "auto" }}
              onClick={() => tasksQuery.refetch()}
            >
              Thử lại
            </button>
          </div>
        ) : taskItems.length === 0 ? (
          <div className={styles.emptyState}>Chưa có việc nào được giao trong chi nhánh.</div>
        ) : filteredTasks.length === 0 ? (
          <div className={styles.emptyState}>
            Không có việc nào ở trạng thái này.{" "}
            <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={() => setTaskStatusFilter("")}>
              Xoá lọc
            </button>
          </div>
        ) : (
          <div className={styles.taskWrap}>
            <table className={styles.taskTable}>
              <thead>
                <tr>
                  <th className={styles.taskColCheck} scope="col">
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      aria-label="Chọn tất cả"
                      checked={selectedTaskIds.size === filteredTasks.length && filteredTasks.length > 0}
                      onChange={() => {
                        if (selectedTaskIds.size === filteredTasks.length) clearTaskSelection();
                        else setSelectedTaskIds(new Set(filteredTasks.map((t) => t.id)));
                      }}
                    />
                  </th>
                  <th scope="col">Việc</th>
                  <th scope="col">Ưu tiên</th>
                  <th scope="col">Giao cho</th>
                  <th scope="col">Hạn</th>
                  <th scope="col">TT</th>
                  <th className={styles.colAct} scope="col">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => {
                  const assigneeName = task.assigned_to_id ? rosterNameById[task.assigned_to_id] : null;
                  const isDone = task.status === "COMPLETED";
                  const menuOpen = taskRowMenuId === task.id;
                  return (
                    <tr key={task.id} className={selectedTaskIds.has(task.id) ? styles.rowSelected : undefined}>
                      <td className={styles.taskColCheck}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          aria-label={`Chọn — ${task.title}`}
                          checked={selectedTaskIds.has(task.id)}
                          onChange={() => toggleTaskSelect(task.id)}
                        />
                      </td>
                      <td className={styles.colName}>{task.title}</td>
                      <td>
                        <span className={`${styles.taskPrio} ${TASK_PRIORITY_CLASS[task.priority]}`}>
                          {TASK_PRIORITY_VI[task.priority]}
                        </span>
                      </td>
                      <td>{assigneeName ?? <span className={styles.stNone}>(chưa giao)</span>}</td>
                      <td>{task.due_date ? formatTaskDueDate(task.due_date) : "—"}</td>
                      <td>
                        <span className={styles.st}>
                          {isDone ? <CheckIcon size={14} /> : task.due_date ? <ClockIcon size={14} /> : "—"}
                        </span>
                      </td>
                      <td className={styles.colAct}>
                        <span className={styles.menuAnchor}>
                          <button
                            type="button"
                            className={styles.btnIcon}
                            aria-haspopup="true"
                            aria-expanded={menuOpen}
                            aria-label={`Thao tác — ${task.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (menuOpen) { setTaskRowMenuId(null); return; }
                              const r = e.currentTarget.getBoundingClientRect();
                              setTaskMenuAnchor({ top: r.top, bottom: r.bottom, right: window.innerWidth - r.right });
                              setTaskRowMenuId(task.id);
                            }}
                          >
                            <DotsIcon />
                          </button>
                          {menuOpen && taskMenuAnchor && (
                            <PortalMenu anchor={taskMenuAnchor} onClose={() => setTaskRowMenuId(null)}>
                              <button
                                type="button"
                                className={styles.menuItem}
                                role="menuitem"
                                onClick={() => { setTaskRowMenuId(null); setTaskDetailItem(task); }}
                              >
                                Xem chi tiết
                              </button>
                              {/* "Đánh dấu hoàn thành" cố tình KHÔNG có trong menu Manager —
                                  complete_task() yêu cầu assigned_to_id === current_user.id
                                  (backend/app/api/v1/tasks.py:858), Manager sẽ luôn 403 —
                                  wireframe §3.1 chỉ đạo omit hẳn thay vì hiện rồi lỗi. */}
                              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => openReassign(task)}>
                                Giao lại
                              </button>
                            </PortalMenu>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  /** [K-FORM] modal tạo + giao việc — wireframe §3.2. */
  const renderCreateTaskModal = () => {
    if (!showCreateTask) return null;
    const submitting = createTaskMutation.isPending;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="createTaskTitle">
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="createTaskTitle">Giao việc mới</h2>
            <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeCreateTask}>
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalBody}>
            {taskFormError && (
              <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: 12 }}>
                {taskFormError}
              </div>
            )}
            <div className={styles.formGrid}>
              <label className={styles.formRow}>
                <span className={styles.formLabel}>Tên việc *</span>
                <input
                  type="text"
                  className={styles.input}
                  value={taskForm.title}
                  disabled={submitting}
                  onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
              <label className={styles.formRow}>
                <span className={styles.formLabel}>Mô tả</span>
                <textarea
                  className={styles.rejectTextarea}
                  value={taskForm.description}
                  disabled={submitting}
                  onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              {/* "Danh mục" (TaskCategory) tạm KHÔNG có trong form v0 — SSOT nhãn VI
                  chưa ratify (UI-LABELS-VI-ENUM.md), xem ghi chú tại const TASK_STATUS_FILTER_OPTIONS phía trên. */}
              <label className={styles.formRow}>
                <span className={styles.formLabel}>Ưu tiên</span>
                <select
                  className={styles.selectBtn}
                  value={taskForm.priority}
                  disabled={submitting}
                  onChange={(e) => setTaskForm((f) => ({ ...f, priority: e.target.value as ManagerTaskPriority }))}
                >
                  {(Object.keys(TASK_PRIORITY_VI) as ManagerTaskPriority[]).map((p) => (
                    <option key={p} value={p}>{TASK_PRIORITY_VI[p]}</option>
                  ))}
                </select>
              </label>
              <div className={styles.formRow}>
                <span className={styles.formLabel}>Hạn hoàn thành</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="date"
                    className={styles.input}
                    value={taskForm.due_date}
                    disabled={submitting}
                    onChange={(e) => setTaskForm((f) => ({ ...f, due_date: e.target.value }))}
                  />
                  <TimeTextInput
                    className={styles.input}
                    value={taskForm.due_time}
                    disabled={submitting || !taskForm.due_date}
                    onChange={(e) => setTaskForm((f) => ({ ...f, due_time: e.target.value }))}
                  />
                </div>
              </div>
              <label className={styles.formRow}>
                <span className={styles.formLabel}>Giao cho</span>
                <select
                  className={styles.selectBtn}
                  value={taskForm.assigned_to_id}
                  disabled={submitting}
                  onChange={(e) => setTaskForm((f) => ({ ...f, assigned_to_id: e.target.value }))}
                >
                  <option value="">Chọn nhân viên…</option>
                  {rosterItems.map((r) => (
                    <option key={r.user_id} value={r.user_id}>{r.full_name}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} disabled={submitting} onClick={closeCreateTask}>
              Huỷ
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={submitting || !taskForm.title.trim()}
              onClick={submitCreateTask}
            >
              {submitting ? "Đang giao…" : "Giao việc"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /** ⋮ "Xem chi tiết" [T3] — CHỈ ĐỌC, dùng lại field đã tải sẵn (không gọi API mới). */
  const renderTaskDetailModal = () => {
    if (!taskDetailItem) return null;
    const task = taskDetailItem;
    const assigneeName = task.assigned_to_id ? rosterNameById[task.assigned_to_id] : null;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="taskDetailTitle">
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="taskDetailTitle">{task.title}</h2>
            <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={() => setTaskDetailItem(null)}>
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.dSec} style={{ borderTop: "none", marginTop: 0, paddingTop: 0 }}>
              {task.description || <span className={styles.stNone}>Không có mô tả.</span>}
            </div>
            <div className={styles.dGrid}>
              <div>Ưu tiên: {TASK_PRIORITY_VI[task.priority]}</div>
              <div>Trạng thái: {TASK_STATUS_VI[task.status]}</div>
              <div>Hạn: {task.due_date ? formatTaskDueDate(task.due_date) : "—"}</div>
              <div>Giao cho: {assigneeName ?? "(chưa giao)"}</div>
            </div>
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setTaskDetailItem(null)}>
              Đóng
            </button>
          </div>
        </div>
      </div>
    );
  };

  /** ⋮ "Giao lại" [T3] — reassign 1 việc (POST /{task_id}/assign), khác bulk bar. */
  const renderReassignModal = () => {
    if (!reassignItem) return null;
    const submitting = assignTaskMutation.isPending;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="reassignTitle">
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="reassignTitle">Giao lại — {reassignItem.title}</h2>
            <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeReassign}>
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalBody}>
            <label className={styles.formRow}>
              <span className={styles.formLabel}>Giao cho</span>
              <select
                className={styles.selectBtn}
                value={reassignTarget}
                disabled={submitting}
                onChange={(e) => setReassignTarget(e.target.value)}
              >
                <option value="">Chọn nhân viên…</option>
                {rosterItems.map((r) => (
                  <option key={r.user_id} value={r.user_id}>{r.full_name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} disabled={submitting} onClick={closeReassign}>
              Huỷ
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={submitting || !reassignTarget}
              onClick={confirmReassign}
            >
              {submitting ? "Đang giao…" : "Xác nhận"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ══════════════════════════════ RENDER ══════════════════════════════

  return (
    <>
      {/* [7] Tiêu đề vùng + [8] Xuất ▼ (đẩy phải, D3) — KHÔNG nút "+ Thêm"
          (không phải màn quản trị user — quyền TA ở SC-007) */}
      <div className={styles.pageHead}>
        <h1 className={styles.pageHeadTitle}>Nhân viên</h1>
        {/* [8] Xuất ▼ — chỉ [T1] (wireframe §1: HIDDEN, không disabled, ở [T2]/[T3] —
            export list nghỉ phép/việc không nằm trong AC của REQ-A/REQ-B v0). */}
        {activeTab === "list" && (
          <div className={styles.pageHeadActions}>
            <span className={styles.menuAnchor}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                aria-haspopup="true"
                aria-expanded={openMenu === "export"}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenu(openMenu === "export" ? null : "export");
                }}
              >
                Xuất
                <ChevronDownIcon />
              </button>
              {openMenu === "export" && (
                <div className={styles.menu} role="menu" aria-label="Xuất danh sách">
                  <button type="button" className={styles.menuItem} role="menuitem" onClick={() => handleExport("xlsx")}>
                    Xuất Excel (.xlsx)
                  </button>
                  <button type="button" className={styles.menuItem} role="menuitem" onClick={() => handleExport("csv")}>
                    Xuất CSV (.csv)
                  </button>
                </div>
              )}
            </span>
          </div>
        )}
      </div>

      {/* ══════════ [T1]/[T2]/[T3] Tab bar — BR-MGR-001 §5.5, wireframe §1 ══════════ */}
      <div className={styles.tabBar} role="tablist" aria-label="Đội ngũ — chế độ xem">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "list"}
          className={`${styles.tab} ${activeTab === "list" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("list")}
        >
          Danh sách
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "leave"}
          className={`${styles.tab} ${activeTab === "leave" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("leave")}
        >
          Yêu cầu nghỉ phép
          <span className={styles.tabBadge}>({pendingLeaveQuery.data?.leave_request_count ?? 0})</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "tasks"}
          className={`${styles.tab} ${activeTab === "tasks" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("tasks")}
        >
          Giao việc
        </button>
      </div>

      {activeTab === "list" && (
        <>
          {/* [1] Tìm theo tên + [2] Khu vực ▼ + [3] đếm */}
          <div className={styles.filterBar}>
            <div className={styles.searchBox}>
              <span className={styles.searchIco} aria-hidden="true"><SearchIcon /></span>
              <input
                type="text"
                className={styles.input}
                placeholder="Tìm theo tên…"
                autoComplete="off"
                aria-label="Tìm theo tên"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className={styles.menuAnchor}>
              <button
                type="button"
                className={styles.selectBtn}
                aria-haspopup="true"
                aria-expanded={openMenu === "area"}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenu(openMenu === "area" ? null : "area");
                }}
              >
                <span>
                  Khu vực: {areaId ? areaOptions.find((a) => a.id === areaId)?.name ?? "Tất cả" : "Tất cả"}
                </span>
                <ChevronDownIcon />
              </button>
              {openMenu === "area" && (
                <div className={styles.menu} role="menu" aria-label="Lọc khu vực">
                  <button
                    type="button"
                    className={`${styles.menuItem} ${areaId === "" ? styles.menuItemActive : ""}`}
                    role="menuitem"
                    onClick={() => { setAreaId(""); setOpenMenu(null); }}
                  >
                    Tất cả
                  </button>
                  {areaOptions
                    .filter((area) => area.id !== "")
                    .map((area) => (
                      <button
                        key={area.id}
                        type="button"
                        className={`${styles.menuItem} ${areaId === area.id ? styles.menuItemActive : ""}`}
                        role="menuitem"
                        onClick={() => { setAreaId(area.id); setOpenMenu(null); }}
                      >
                        {area.name}
                      </button>
                    ))}
                </div>
              )}
            </span>
            {/* [3] Đếm: N nhân viên · M có ca hôm nay */}
            <span className={styles.staffCount}>
              {data ? `${data.total_staff} nhân viên · ${data.scheduled_today} có ca hôm nay` : ""}
            </span>
          </div>

          {/* ══════════ [5] BẢNG đội ngũ (gom nhóm khu vực; "Chưa phân khu vực" flat lên đầu) ══════════ */}
          {listQuery.isLoading ? (
            <div aria-busy="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} />
              ))}
            </div>
          ) : listQuery.isError ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              Không tải được dữ liệu. Thử lại
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                style={{ marginLeft: "auto" }}
                onClick={() => listQuery.refetch()}
              >
                Thử lại
              </button>
            </div>
          ) : !data || data.total_staff === 0 ? (
            <div className={styles.emptyState}>Chưa có nhân viên trong đơn vị.</div>
          ) : visibleGroups.length === 0 ? (
            <div className={styles.emptyState}>Không tìm thấy nhân viên phù hợp.</div>
          ) : (
            <>
              {renderTable()}
              {renderCards()}
            </>
          )}
        </>
      )}

      {/* ══════════ [T2] Yêu cầu nghỉ phép (REQ-A) ══════════ */}
      {activeTab === "leave" && renderLeaveTab()}

      {/* ══════════ [T3] Giao việc (REQ-B) ══════════ */}
      {activeTab === "tasks" && renderTasksTab()}

      {/* ══════════ [D] MODAL "Chi tiết nhân viên" — CHỈ ĐỌC ══════════ */}
      {renderDetailModal()}
      {renderCreateTaskModal()}
      {renderTaskDetailModal()}
      {renderReassignModal()}

      {/* Toast (pattern window.nqhToast — mockup common.js) */}
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </>
  );
};

export default ManagerStaffPage;
